/**
 * Appointment reminder sync payloads (#2243).
 *
 * Confirmed empirically on the dev backend: appointment CRUD creates NO
 * reminder rows server-side (unlike medications, whose create embeds a
 * reminder config), and the old client helper for appointment reminders had
 * zero consumers — so appointments simply never reminded. The single source
 * of truth is the reminder RESOURCE (`/me/reminders`, `type:"appointment"`,
 * linked via `appointmentId`), kept in sync by the appointment mutations —
 * the one layer every entry point goes through.
 *
 * Lead-time math is pure wall-clock arithmetic on the appointment's own
 * date/time strings (no Date construction), so it cannot drift through the
 * device timezone and is deterministic to test, including day borrows.
 */

import { serializeMedicationLogScheduledFor } from "./medication-log-date";

export const DEFAULT_APPOINTMENT_REMINDER_MINUTES = 60;

export type AppointmentReminderPayload = {
  type: "appointment";
  title: string;
  message: string;
  appointmentId: string;
  frequency: "once";
  times: string[];
  startDate: string;
  timezone?: string;
  pushToken?: string | null;
};

function decrementDateString(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const previous = new Date(Date.UTC(y!, m! - 1, d! - 1));
  const yy = previous.getUTCFullYear();
  const mm = String(previous.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(previous.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Compute the reminder's wall-clock date + HH:mm, `minutesBefore` ahead of
 * the appointment start, borrowing a day when the lead time crosses
 * midnight.
 */
export function reminderMomentBefore(
  startDate: string,
  startTime: string,
  minutesBefore: number,
): { date: string; time: string } {
  const [hours, minutes] = startTime.split(":").map(Number);
  let total = hours! * 60 + minutes! - minutesBefore;
  let date = startDate;
  while (total < 0) {
    total += 24 * 60;
    date = decrementDateString(date);
  }
  const h = String(Math.floor(total / 60)).padStart(2, "0");
  const m = String(total % 60).padStart(2, "0");
  return { date, time: `${h}:${m}` };
}

export function buildAppointmentReminderPayload(options: {
  appointmentId: string;
  title: string;
  startDate: string;
  /** HH:mm — no reminder is built for appointments without a start time. */
  startTime: string;
  timezone?: string;
  minutesBefore?: number;
  pushToken?: string | null;
}): AppointmentReminderPayload {
  const minutesBefore =
    options.minutesBefore ?? DEFAULT_APPOINTMENT_REMINDER_MINUTES;
  const moment = reminderMomentBefore(
    options.startDate,
    options.startTime,
    minutesBefore,
  );
  // The reminders endpoint validates startDate as a strict ISO instant
  // (same zod contract that broke dose logging in #2357) — the wall-clock
  // moment converts through the shared DST-safe serializer.
  const startInstant = serializeMedicationLogScheduledFor(
    `${moment.date}T${moment.time}:00`,
    options.timezone,
  );
  return {
    type: "appointment",
    title: `Upcoming: ${options.title}`,
    message: `Your appointment is in ${minutesBefore} minutes`,
    appointmentId: options.appointmentId,
    frequency: "once",
    times: [moment.time],
    startDate: startInstant ?? `${moment.date}T${moment.time}:00`,
    timezone: options.timezone,
    pushToken: options.pushToken,
  };
}

/** Find the reminder rows linked to an appointment. */
export function findAppointmentReminders<
  T extends { appointmentId?: string | null },
>(reminders: readonly T[] | undefined, appointmentId: string): T[] {
  return (reminders ?? []).filter(
    (reminder) => reminder.appointmentId === appointmentId,
  );
}
